import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import {
  Clock,
  Award,
  Star,
  ChevronRight,
  Phone,
  CheckCircle2,
  Users,
} from "lucide-react";
import { Constants } from "@/constants/constants";
import { IconMap } from "@/constants/IconMap";
import store  from '@/assets/store2.jpg'
import FoldedLaundry from '@/assets/folded_laundry.jfif'

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const HomePage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-gradient min-h-[90vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-72 h-72 bg-sky-200 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-sky-200 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div 
              className="space-y-8"
              initial="initial"
              animate="animate"
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="space-y-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-700 rounded-full text-sm font-medium">
                  <Award className="w-4 h-4" />
                  {Constants.AWARD_WINNING_SINCE_YEAR}
                </span>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight">
                  {Constants.LAUNDRY_AND}<br />
                  <span className="gradient-text">{Constants.MORE}</span>
                </h1>
                <p className="text-lg md:text-xl text-slate-600 max-w-xl">
                  {Constants.BUSINESS_DESCRIPTION}
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Link to="/booking">
                  <Button 
                    size="lg" 
                    className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 shadow-lg shadow-sky-600/20 group"
                    data-testid="hero-book-btn"
                  >
                    {Constants.BOOK_A_PICKUP}
                    <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="tel:519-210-9274">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 border-slate-300 hover:bg-slate-50"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {Constants.SCRUBBLES_PHONE}
                  </Button>
                </a>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">{Constants.TRUSTED_CUSTOMERS}</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={store}
                  alt="Scrubbles laundromat interior"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{Constants.TRUSTED_CUSTOMERS}</p>
                    <p className="text-sm text-slate-500">{Constants.X_AWARD_WINNER}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">{Constants.OUR_SERVICES}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              {Constants.EVERYTHING_YOUR_LAUNDRY_NEEDS}
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              {Constants.FROM_SELF_SERVE}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {IconMap.HomePage.services.map((service, index) => (
              <Link to={service.link} key={service.title}>
                <Card 
                  className={`service-card h-full border-slate-100 hover:border-sky-100 ${
                    service.highlight ? "ring-2 ring-sky-500 ring-offset-2" : ""
                  }`}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      service.highlight 
                        ? "bg-gradient-to-br from-sky-500 to-sky-600 text-white" 
                        : "bg-sky-50 text-sky-600"
                    }`}>
                      <service.icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">{service.title}</h3>
                      <p className="text-slate-500 text-sm mt-1">{service.description}</p>
                    </div>
                    <div className="pt-2 flex items-center justify-between">
                      <span className="text-sky-600 font-semibold">{service.price}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                    {service.highlight && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          {Constants.POPULAR}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="rounded-full px-8" data-testid="view-all-services-btn">
                {Constants.VIEW_ALL_SERVICES}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">{Constants.WHY_CHOOSE_US}</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
                {Constants.SO_MUCH_MORE_THAN}
              </h2>
              <p className="text-slate-600 mt-4 text-lg">
                {Constants.SCRUBBLES_HISTORY}
              </p>

              <div className="mt-8 space-y-4">
                {[
                  "Record Readers' Choice Award Winner",
                  "Lowest price guaranteed in the region",
                  "Free pickup & delivery within 25km",
                  "100% customer satisfaction",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-sky-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                {IconMap.HomePage.features.map((feature) => (
                  <div
                    key={feature.text}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-100"
                  >
                    <feature.icon className="w-4 h-4 text-sky-600" />
                    <span className="text-sm text-slate-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={FoldedLaundry}
                alt="Folded towels"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-sky-600 text-white rounded-2xl p-6 shadow-xl">
                <p className="text-4xl font-bold">{Constants.FIFTEEN_PLUS}</p>
                <p className="text-sky-100">{Constants.YEARS_OF_SERVICE}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">{Constants.TESTIMONIALS}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              {Constants.WHAT_OUR_CUSTOMERS_SAY}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {IconMap.HomePage.testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card border-slate-100" data-testid={`testimonial-${index}`}>
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 leading-relaxed">{testimonial.text}</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={testimonial.image} className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white font-bold"/>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Gallery */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">{Constants.RECOGNITION}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Record Reader's Choice Awards
            </h2>
            <p className="text-slate-600 mt-4">
              Proud winners for multiple consecutive years
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {IconMap.HomePage.awards.map((award, index, originalArray) => (
              <div
                key={award.year}
                className="award-badge group cursor-pointer"
                data-testid={`award-${award.year}`}
              >
                <div className={index === originalArray.length - 1 
                  ? "group-hover:transition-all"
                  : "bg-white rounded-xl p-3 shadow-sm border border-slate-100 group-hover:shadow-lg transition-all"}>
                  <img
                    src={award.image}
                    alt={`Record Reader Award ${award.year}`}
                    className="w-full h-auto rounded-lg"
                  />
                  <p className="text-center mt-2 font-semibold text-slate-700">{award.year}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/awards">
              <Button variant="outline" className="rounded-full px-8" data-testid="view-all-services-btn">
                View All Awards
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Find Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
                Visit Our Location
              </h2>
              <p className="text-slate-600 mt-4 text-lg">
                Located in the heart of Elmira, we serve customers within a 25km radius.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Opening Hours</p>
                    <p className="text-slate-600 text-sm">Open Daily: 6am - 11pm</p>
                    <br />
                    <p className="font-semibold text-slate-900">Attended Service Hours</p>
                    <p className="text-slate-600 text-sm">Wednesday & Friday: 10am - 6pm</p>
                    <p className="text-slate-600 text-sm">Saturday & Sunday: 10am - 5pm</p>
                    <p className="text-slate-600 text-sm">Monday, Tuesday & Thursday: Self-serve only</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Address</p>
                    <p className="text-slate-600 text-sm ">7-112 Oriole Parkway</p>
                    <p className="text-slate-600 text-sm">Elmira, Ontario N3B 1C5</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/booking">
                  <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="map-book-btn">
                    Book a Pickup
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div data-testid="service-area-map">
              <ServiceAreaMap height="450px" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="text-sky-100 mt-4 text-lg">
            Book your first pickup today and experience the Scrubbles difference.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/booking">
              <Button 
                size="lg" 
                className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8"
                data-testid="cta-book-btn"
              >
                Book a Pickup
              </Button>
            </Link>
            <a href="tel:519-210-9274">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="cta-call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
